import React, { useEffect, useState } from 'react';
import { Typography, Button, Box, Dialog, DialogTitle, DialogContent, IconButton, Menu, MenuItem, DialogActions, TextField, InputAdornment } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';

import Breadcrumb from 'src/layouts/full/shared/breadcrumb/Breadcrumb';
import PageContainer from 'src/components/container/PageContainer';
import DataTable from '../../../components/DataTable/DataTable';
import toast from 'react-hot-toast';
import AddCustomerForm from '../../../components/AddCustomerForm';
import { MoreVert, Search } from '@mui/icons-material';
import { fdelete, fget, fpost, fput } from '../../../axios/axiosCalls';
import CustomerEditDrawer from '../../../components/CustomerEditDrawer';
import { debounce } from 'lodash';



const BCrumb = [
  {
    to: '/',
    title: 'Home',
  },
  {
    title: 'Students',
  },
];

const Customers = () => {
  const DeleteConfirmDialog = ({ open, onClose, onConfirm }) => (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Confirm Deletion</DialogTitle>
      <DialogContent>Are you sure you want to delete this Student?</DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={onConfirm} color="error">Delete</Button>
      </DialogActions>
    </Dialog>
  );
  
  // Add ActionMenu component
  const ActionMenu = ({ customer, onEdit, onDelete ,row}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
    const handleClick = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);
    const handleEditClick = () => {
      onEdit(customer);
      handleClose();
    };
    const handleDeleteClick = () => {
      setDeleteDialogOpen(true);
      handleClose();
    };
  
    return (
      <>
        <IconButton onClick={handleClick}>
          <MoreVert />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
        >
          <MenuItem onClick={handleEditClick}>Edit</MenuItem>
          <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
            Delete
          </MenuItem>
        </Menu>
        <DeleteConfirmDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={() => {
            console.log(row)
            
            onDelete(customer);
            setDeleteDialogOpen(false);
          }}
        />
      </>
    );
  };
  
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editCustomer, setEditCustomer] = useState(null);


  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Update handleEdit
  const handleEdit = (customer) => {
    setSelectedCustomer(customer);
    setDrawerOpen(true);
  };
  const handleDelete = async (customer) => {
    try {
      
      const response = await fdelete({
        url: `/api/v2/channelpartner/students/${customer?.studentId}`
      });
      if (response.data.status === 'Success') {
        toast.success('Student deleted successfully');
        fetchData(page, rowsPerPage);
      } else {
        toast.error('Failed to delete Student');
      }
    } catch (error) {
      toast.error(error?.response.data.message??'An error occurred while deleting Student');
      console.error('Update Error:', error);

    }
  };
  const [searchQuery, setSearchQuery] = useState('');

  const debouncedSearch = debounce((query) => {
    setSearchQuery(query);
    setPage(0); 
    fetchData(0, rowsPerPage, query);
  }, 500);
  const columns = [
    { field: 'name', headerName: 'Student', flex: 0.5 },
    { field: 'type', headerName: 'Type', flex: 0.5 },
    { field: 'resCountry', headerName: 'Country', flex: 0.5 },
    { field: 'dob', headerName: 'Date of Birth', flex: 0.5 },
    { field: 'college', headerName: 'College', flex: 0.5 },
    { 
      field: 'actions', 
      headerName: 'Actions', 
      flex: 0.3,
      renderCell: (params) => (
        <ActionMenu 
          customer={params} 
          onEdit={handleEdit}
          onDelete={handleDelete}
          row={params}
        />
      )
    }
  ];
  const handleSaveEdit = async (formData) => {
    try {
      const response = await fpost({
        url: `/api/v2/channelpartner/students/${formData.studentId}`,
        data: {
          ...formData,
          campus: formData.campusId,
          college: formData.collegeId,
          guarantor:{
            name: formData.guarantorName,
            email: formData.guarantorEmail,
            phone: formData.guarantorPhone,
            address: formData.guarantorAddress,
            country: formData.guarantorCountry,
            type: formData.guarantorType
          }
        }
      });
      
      if (response.data.status === 'Success') {
        toast.success('Student updated successfully');
        setDrawerOpen(false);
        fetchData(page, rowsPerPage);
      } else {
        toast.error('Failed to update Student');
      }
    } catch (error) {
      toast.error(Object.values(error?.response?.data?.errors || {})[0] || 
        error?.response?.data?.message || 'An error occurred while updating Student');
      console.error('Update Error:', error);
    }
  };

  const [totalRows, setTotalRows] = useState(0);
  const [open, setOpen] = useState(false)
  const fetchData = async (page, limit, search = '') => {
    setLoading(true);
    try {
      const response = await fget({ 
        url: `/api/v2/channelpartner/students`,
        params: {
          page: page + 1,
          limit,
          term: search
        }
      });
      
      setRows(response.data.students);
      setTotalRows(response.data.pagination.totalCount);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };
  
  // Update useEffect

  const handleOpen = () => {
    setOpen(true); 
  };
  
  const handleClose = () => {
    setOpen(false);
    fetchData(); // Refresh data after adding a customer
  };
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  useEffect(() => {
    fetchData(page, rowsPerPage, searchQuery);
  }, [page, rowsPerPage, searchQuery]);

  return (
    <PageContainer title="Students" description="Students">
      <Breadcrumb title="Students" items={BCrumb} />
      
      <Box 
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: { xs: 'stretch', sm: 'flex-end' },
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
          mb: 2
        }}
      >
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleOpen}
          sx={{ 
            width: { xs: '100%', sm: 'auto' }
          }}
        >
          Add Student
        </Button>

        <TextField
          fullWidth
          placeholder="Search students..."
          onChange={(e) => debouncedSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="end">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: 'primary.main',
              },
            },
            width: { xs: '100%', sm: 200 }
          }}
        />
      </Box>

      <Box style={{ height: 400, width: '100%', }}>
        <DataTable
          rows={rows}
          columns={columns}
          loading={loading}
          totalRows={totalRows}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Box>
      
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Student</DialogTitle>
        <DialogContent>
          <AddCustomerForm onSuccess={handleClose} />
        </DialogContent>
      </Dialog>
      <CustomerEditDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        customer={selectedCustomer}
        onSave={handleSaveEdit}
      />
    </PageContainer>
  );
};
export default Customers;


